"use client";

import Link from "next/link";
import { useRef, useState, type MouseEvent, type ReactNode } from "react";

import {
  formFieldEntries,
  type FormFields,
} from "@/components/office/form-fields";
import { AlertDialog } from "@/components/office/Modal";

/**
 * A small framed label — a serial, a tag — with an optional remove segment:
 * the × sits behind a vertical hairline that meets the chip's top and bottom
 * rules, and its hitbox is the whole segment right of the divider, not the
 * glyph. The remove either calls back (client state) or submits a server
 * action with hidden fields; `confirm` asks first, through the house
 * AlertDialog.
 */
export function Chip({
  href,
  children,
  remove,
  className = "",
}: {
  href?: string;
  children: ReactNode;
  remove?: {
    ariaLabel: string;
    disabled?: boolean;
    /** Ask before removing: the question, and what a yes does. */
    confirm?: { title: string; message: string };
    onClick?: () => void;
    action?: (formData: FormData) => Promise<void>;
    /** Hidden inputs the action submits. A field holding several values
     *  emits one input each, so the action reads them with `getAll` — a
     *  joined string would arrive as one bogus value. */
    fields?: FormFields;
    /** Make the WHOLE chip the hitbox instead of the × segment alone. For a
     *  chip whose label leads nowhere — a picked tag, a blackout's target —
     *  where the only thing a click could mean is "drop this". Takes a
     *  callback or an `action` like any other remove. A chip with an `href`
     *  has two meanings and keeps the two hitboxes. */
    anywhere?: boolean;
  };
  className?: string;
}) {
  // 0.6em is ONE space in the mono face the chip is set in, so its text is
  // inset by the same whitespace that separates the words inside it, at
  // whatever size the chip is set.
  const frame =
    "inline-flex items-stretch overflow-hidden rounded-sm border border-line font-mono text-[11px] leading-5 text-fg";
  // The label pads one pixel above and three below, not two and two: the mono
  // face's words sit about a pixel under the middle of their line box, and a
  // whole pixel (the engine snaps a baseline to the pixel grid) lifts them to
  // the middle of the chip. The same height.
  const labelPad = "px-[0.6em] pt-px pb-0.75";
  const formRef = useRef<HTMLFormElement>(null);
  const [asking, setAsking] = useState(false);
  // What removing does, once it is meant: submit the action, or call back.
  const act = () =>
    remove?.action ? formRef.current?.requestSubmit() : remove?.onClick?.();
  // With a question to ask, the press opens the dialog and its verb acts;
  // without one the press acts (a submit button submits by itself).
  const press = (e: MouseEvent) => {
    if (remove?.confirm) {
      e.preventDefault();
      setAsking(true);
    } else if (!remove?.action) act();
  };
  const dialog = remove?.confirm && (
    <AlertDialog
      open={asking}
      onCancel={() => setAsking(false)}
      onConfirm={() => {
        setAsking(false);
        act();
      }}
      title={remove.confirm.title}
      description={remove.confirm.message}
      confirmLabel="Remove"
      variant="danger"
    />
  );
  // A server-action remove submits; the hidden fields say WHICH row. Shared by
  // both hitbox shapes — the whole-chip one used to drop `action` on the floor
  // and render a control that fired nothing.
  const submitting = (node: ReactNode) =>
    remove?.action ? (
      <form ref={formRef} action={remove.action} className="contents">
        {formFieldEntries(remove.fields ?? {}).map(([name, value], index) => (
          <input
            key={`${name}:${index}`}
            type="hidden"
            name={name}
            value={value}
          />
        ))}
        {node}
      </form>
    ) : (
      node
    );
  // Whole-chip remove: one button, the divider and × drawn inside it, so the
  // look matches the two-hitbox chip and only the target grows.
  if (remove?.anywhere && !href) {
    const whole = submitting(
      <button
        type={remove.action ? "submit" : "button"}
        disabled={remove.disabled}
        aria-label={remove.ariaLabel}
        title={remove.ariaLabel}
        onClick={press}
        className={`group ${frame} cursor-pointer transition-colors hover:border-fg-subtle disabled:cursor-default disabled:opacity-40 ${className}`.trim()}
      >
        <span className={`min-w-0 truncate ${labelPad}`}>{children}</span>
        {/* Centred, not just stretched: the × segment carries no vertical
            padding of its own, so it grows to the label's height with the
            glyph pinned at the top of the box the stretch gives it. */}
        <span
          aria-hidden
          className="flex shrink-0 items-center border-l border-line px-[0.6em] text-fg-subtle transition-colors group-hover:bg-fg/10 group-hover:text-fg"
        >
          ×
        </span>
      </button>,
    );
    return (
      <>
        {whole}
        {dialog}
      </>
    );
  }

  const label = href ? (
    <Link
      href={href}
      className={`${labelPad} transition-colors hover:text-fg-muted`}
    >
      {children}
    </Link>
  ) : (
    <span className={labelPad}>{children}</span>
  );
  // Centred for the same reason as the whole-chip ×: the segment takes its
  // height from the label beside it and owns no vertical padding.
  const button = remove && (
    <button
      type={remove.action ? "submit" : "button"}
      disabled={remove.disabled}
      aria-label={remove.ariaLabel}
      title={remove.ariaLabel}
      onClick={press}
      className="inline-flex cursor-pointer items-center border-l border-line px-[0.6em] text-fg-subtle transition-colors hover:bg-fg/10 hover:text-fg disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent"
    >
      ×
    </button>
  );
  // A div, not a span: the remove segment can be a <form>, which phrasing
  // content (and a <p> parent) can't hold — React flags it at hydration.
  // The dialog sits beside the chip, not in it: outside the remove's form.
  return (
    <>
      <div className={`${frame} ${className}`.trim()}>
        {label}
        {submitting(button)}
      </div>
      {dialog}
    </>
  );
}
